import React, { useState, useEffect } from 'react';
import './App.css';
import ReviewPage from './ReviewPage';
import MockReviewPage from './MockReviewPage';
import ReceiptsPage from './ReceiptsPage';
import MockReceiptsPage from './MockReceiptsPage';
import MileagePage from './MileagePage';
import MockMileagePage from './MockMileagePage';
import HelpPage from './HelpPage';
import VerifyExpiredPage from './VerifyExpiredPage';
import PricingPage from './PricingPage';
import SuccessPage from './SuccessPage';

import WhyChooseZap from './components/tools/WhyChooseZap';
import MtdGuidePage from './app/mtd-2026-guide/page';
import PropertyDecisionTree from './components/tools/PropertyDecisionTree';
import MileageComparator from './components/tools/MileageComparator';
import EfficiencyTimer from './components/tools/EfficiencyTimer';
import OnboardingGuide from './pages/partners/onboarding-guide';
import MarginCalculator from './pages/partners/margin-calculator';
import JointPropertyChecker from './pages/tools/joint-property-checker';
import MTDStatusBanner from './components/ui/MTDStatusBanner';
import Breadcrumbs from './components/ui/Breadcrumbs';
import ForeignPropertyIncome from './pages/mtd-anxieties/foreign-property-income';
import LandlordsJointOwnership from './pages/mtd-anxieties/mtd-for-landlords-joint-ownership';
import DigitalLinksExplained from './pages/mtd-anxieties/mtd-digital-links-explained';
import ExemptionCriteria from './pages/mtd-anxieties/mtd-exemption-criteria';
import GoogleAnalytics, { trackEvent } from './components/analytics/GoogleAnalytics';
import ConsentBanner from './components/analytics/ConsentBanner';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import config from './config';
import { POSITIONING } from './compliance/mtdFacts';

// Use central config
const { API_BASE, ENABLE_MOCKS, PUBLIC_ROUTES } = config;

axios.defaults.withCredentials = true;



// Global 401 Interceptor
axios.interceptors.response.use(
    response => response,
    error => {
        if (error.response && error.response.status === 401) {
            const path = window.location.pathname;
            // Only redirect if we are NOT on a public route
            if (!PUBLIC_ROUTES.includes(path)) {
                window.location.href = '/';
            }
        }
        return Promise.reject(error);
    }
);

const extractErrorMessage = (err, fallback = 'Something went wrong.') => {
    const msg = err.response?.data?.detail || err.response?.data || fallback;
    return typeof msg === 'object' ? JSON.stringify(msg) : msg;
};

function App() {
    const [view, setView] = useState('home');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [accountantEmail, setAccountantEmail] = useState('');
    const [signupStatus, setSignupStatus] = useState('idle');
    const [signupMessage, setSignupMessage] = useState('');
    const [addPhone, setAddPhone] = useState('');
    const [whatsappAction, setWhatsappAction] = useState('idle');
    const [whatsappMessage, setWhatsappMessage] = useState('');
    const [addAccountantEmail, setAddAccountantEmail] = useState('');
    const [accountantAction, setAccountantAction] = useState('idle');
    const [accountantMessage, setAccountantMessage] = useState('');
    const [lastSignupData, setLastSignupData] = useState(null);
    const [resendTimer, setResendTimer] = useState(0);
    const [resendStatus, setResendStatus] = useState('idle'); // idle, loading, success, error
    const [user, setUser] = useState(null);


    useEffect(() => {
        // Global TaxService Schema for SEO/GEO
        const globalSchema = {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Zap Expenses",
            "alternateName": "ZapExpenses",
            "description": "Receipt capture and structured record preparation via WhatsApp and email. Works alongside compatible accounting or filing software.",
            "url": "https://zapexpenses.com",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web, WhatsApp",
            "provider": {
                "@type": "Organization",
                "name": "Zap Expenses",
                "url": "https://zapexpenses.com",
                "logo": "https://zapexpenses.com/logo.png"
            },
            "offers": {
                "@type": "Offer",
                "price": "4.95",
                "priceCurrency": "GBP",
                "description": "Starting from £4.95/mo. Free 30-day trial available."
            }
        };
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'global-tax-service-schema';
        script.innerHTML = JSON.stringify(globalSchema);
        document.head.appendChild(script);

        const checkAuth = async () => {
            try {
                const response = await axios.get(`${API_BASE}/auth/me`, { timeout: 4000 });
                if (response.data.logged_in) {
                    setUser(response.data);
                    if (response.data.accountant_email) {
                        setAddAccountantEmail(response.data.accountant_email);
                    }
                }
            } catch (err) {
                // Unauthenticated, blocked, or timed out — keep the public homepage.
            }
        };
        checkAuth();

        return () => {
            const el = document.getElementById('global-tax-service-schema');
            if (el) document.head.removeChild(el);
        };
    }, []);

    useEffect(() => {
        let interval;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    const handleSignup = async (e) => {
        e.preventDefault();
        setSignupStatus('loading');
        setSignupMessage('');

        try {
            const formData = new FormData();
            formData.append('email', email);
            if (phone.trim()) {
                const formatted = formatPhoneNumber(phone.trim());
                formData.append('phone', formatted);
            }
            if (accountantEmail.trim()) {
                formData.append('accountant_email', accountantEmail.trim());
            }
            await axios.post(`${API_BASE}/auth/signup`, formData);

            // Track WhatsApp conversion start if phone is provided
            if (phone.trim()) {
                trackEvent('whatsapp_conversion_start', { source: 'signup_form' });
            }

            setSignupStatus('success');
            const msg = phone.trim()
                ? (
                    <>
                        <p className="mb-3">Magic link sent to your email! We've also sent a WhatsApp verification to your number.</p>
                        <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-amber-200/80 text-xs text-left">
                            <span className="mt-0.5">📬</span>
                            <span>Can't find it? Check your <strong className="text-amber-200">spam or junk folder</strong> — it should arrive within a minute.</span>
                        </div>
                    </>
                )
                : 'Magic link sent! Check your spam/junk folder if it doesn\'t arrive within a minute.';
            setSignupMessage(msg);
            setLastSignupData({ email, phone, accountantEmail });
            setEmail('');
            setPhone('');
            setAccountantEmail('');
        } catch (err) {
            console.error('Signup error:', err);
            if (err.response) {
                console.error('Response status:', err.response.status);
                console.error('Response data:', err.response.data);
            }
            setSignupStatus('error');
            setSignupMessage(extractErrorMessage(err, 'Something went wrong. Please try again.'));
        }
    };

    const handleResendSignup = async () => {
        if (resendTimer > 0 || !lastSignupData) return;
        setResendStatus('loading');
        try {
            const formData = new FormData();
            formData.append('email', lastSignupData.email);
            if (lastSignupData.phone && lastSignupData.phone.trim()) {
                const formatted = formatPhoneNumber(lastSignupData.phone.trim());
                formData.append('phone', formatted);
            }
            if (lastSignupData.accountantEmail && lastSignupData.accountantEmail.trim()) {
                formData.append('accountant_email', lastSignupData.accountantEmail.trim());
            }
            await axios.post(`${API_BASE}/auth/signup`, formData);
            setResendStatus('success');
            setResendTimer(60);
        } catch (err) {
            setResendStatus('error');
            console.error('Resend error:', err);
        }
    };

    const handleAddWhatsApp = async (e) => {
        e.preventDefault();
        if (!addPhone.trim()) return;
        setWhatsappAction('loading');
        setWhatsappMessage('');
        try {
            const formData = new FormData();
            formData.append('phone', formatted);
            await axios.post(`${API_BASE}/auth/add-contact`, formData);

            trackEvent('whatsapp_conversion_start', { source: 'add_whatsapp_form' });

            // Now initiate verification
            await axios.post(`${API_BASE}/auth/initiate-whatsapp-verify`);
            setWhatsappAction('pending');
            setWhatsappMessage('Verification sent! Check your WhatsApp.');
            // Refresh user data
            const response = await axios.get(`${API_BASE}/auth/me`);
            if (response.data.logged_in) setUser(response.data);
        } catch (err) {
            console.error('Add WhatsApp error:', err);
            setWhatsappAction('error');
            setWhatsappMessage(extractErrorMessage(err));
        }
    };

    const handleAddAccountant = async (e) => {
        e.preventDefault();
        if (!addAccountantEmail.trim()) return;
        setAccountantAction('loading');
        setAccountantMessage('');
        try {
            await axios.post(`${API_BASE}/auth/add-contact`, { accountant_email: addAccountantEmail.trim() });
            setAccountantAction('success');
            setAccountantMessage('Accountant email saved!');
            // Refresh user data
            const response = await axios.get(`${API_BASE}/auth/me`);
            if (response.data.logged_in) {
                setUser(response.data);
            }
        } catch (err) {
            console.error('Add Accountant error:', err);
            setAccountantAction('error');
            setAccountantMessage(extractErrorMessage(err));
        }
    };

    const handleResendVerify = async () => {
        setWhatsappAction('loading');
        setWhatsappMessage('');
        try {
            await axios.post(`${API_BASE}/auth/initiate-whatsapp-verify`);
            setWhatsappAction('pending');
            setWhatsappMessage('Verification re-sent! Check your WhatsApp.');
        } catch (err) {
            console.error('Resend verify error:', err);
            setWhatsappAction('error');
            setWhatsappMessage(extractErrorMessage(err));
        }
    };

    const handleLogout = async () => {
        try {
            await axios.post(`${API_BASE}/auth/logout`);
            setUser(null);
            setView('home'); // Reset view to home
            setPhone('');
            setAddPhone('');
            setWhatsappAction('idle');
            setWhatsappMessage('');
            setAddAccountantEmail('');
            setAccountantAction('idle');
            setAccountantMessage('');
            // Redirect to home
            window.location.href = '/';
        } catch (err) {
            console.error('Logout error:', err);
            // Force local logout anyway
            setUser(null);
            setView('home');
            setPhone('');
            setAddPhone('');
            setWhatsappAction('idle');
            setWhatsappMessage('');
            setAddAccountantEmail('');
            setAccountantAction('idle');
            setAccountantMessage('');
            // Redirect to home
            window.location.href = '/';
        }
    };

    const handleDeleteAccount = async () => {
        if (!window.confirm("Are you sure you want to delete your account? This action cannot be undone.")) return;

        try {
            await axios.delete(`${API_BASE}/auth/delete-account`);
            window.location.href = '/';
        } catch (err) {
            console.error('Delete account error:', err);
            alert(extractErrorMessage(err, "Deletion failed"));
        }
    };

    const formatPhoneNumber = (raw) => {
        if (!raw) return '';
        // Remove all non-numeric chars except +
        let cleaned = raw.replace(/[^\d+]/g, '');

        // Handle UK numbers starting with 0
        if (cleaned.startsWith('0')) {
            cleaned = '+44' + cleaned.substring(1);
        }
        // Handle numbers starting with 44 (without +)
        else if (cleaned.startsWith('44')) {
            cleaned = '+' + cleaned;
        }

        return cleaned;
    };


    useEffect(() => {
        const path = window.location.pathname;

        // Mock Routes (Protected by Toggle)
        if (ENABLE_MOCKS) {
            if (path === '/mock') {
                setView('mock_index');
                return;
            } else if (path === '/mockreview') {
                setView('mockreview');
                return;
            } else if (path === '/mockreceipts') {
                setView('mockreceipts');
                return;
            } else if (path === '/mockmileage') {
                setView('mockmileage');
                return;
            }
        }

        // Production Routes
        if (path === '/logout') {
            // Will be handled after render
            setView('logout');
        } else if (path === '/review') {
            setView('review');
        } else if (path === '/receipts') {
            setView('receipts');
        } else if (path === '/mileage') {
            setView('mileage');
        } else if (path === '/verify-expired') {
            setView('verify_expired');
        } else if (path === '/help' || path === '/support') {
            setView('help');
        } else if (path === '/pricing') {
            setView('pricing');
        } else if (path === '/mtd-2026-guide' || path === '/help/mtd-overview') {
            setView('mtd-guide');
        } else if (path === '/tools/property-allowance') {
            setView('property-allowance');
        } else if (path === '/tools/mileage-calculator') {
            setView('mileage-calculator');
        } else if (path === '/speed-test') {
            setView('speed-test');
        } else if (path === '/why-choose-zap') {
            setView('why-choose-zap');
        } else if (path === '/success') {
            setView('success');
        } else if (path === '/partners/onboarding-guide') {
            setView('partners-onboarding');
        } else if (path === '/partners/margin-calculator') {
            setView('partners-margin');
        } else if (path === '/tools/joint-property-checker' || path === '/tools/compliance-checker') {
            setView('joint-property-checker');
        } else if (path === '/mtd-anxieties/foreign-property-income') {
            setView('foreign-property-income');
        } else if (path === '/mtd-anxieties/mtd-for-landlords-joint-ownership') {
            setView('landlords-joint-ownership');
        } else if (path === '/mtd-anxieties/mtd-digital-links-explained') {
            setView('digital-links-explained');
        } else if (path === '/mtd-anxieties/mtd-exemption-criteria') {
            setView('exemption-criteria');
        }
    }, []);

    // Trigger logout when view is set to 'logout'
    useEffect(() => {
        if (view === 'logout') {
            handleLogout();
        }
    }, [view]);

    // Per-page SEO: update title, description, canonical and JSON-LD on view change
    useEffect(() => {
        const pageMeta = {
            home: {
                title: 'ZapExpenses — Send Receipts by WhatsApp | Structured Records for Your Accountant',
                description: 'Send receipts by WhatsApp or email. Zap extracts the details and prepares structured records ready for your accountant or compatible filing software. No apps, no dashboards.',
                canonical: 'https://zapexpenses.com/',
                jsonld: { '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: 'ZapExpenses', applicationCategory: 'BusinessApplication', operatingSystem: 'Web, WhatsApp', description: 'Receipt capture and structured record preparation via WhatsApp and email. Works alongside compatible accounting or filing software.', url: 'https://zapexpenses.com/', offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP', description: '30-day free trial, no credit card required' } }
            },
            'why-choose-zap': {
                title: 'Why Choose Zap? | ZapExpenses vs Accounting Apps',
                description: 'See why Zap beats traditional accounting apps for sole traders. WhatsApp and email capture, structured records ready for your accountant, alongside compatible filing software.',
                canonical: 'https://zapexpenses.com/why-choose-zap',
                jsonld: { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: [{ '@type': 'Question', name: 'Do I still need filing software with Zap?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Zap prepares structured records for export. You or your accountant use compatible filing software to submit quarterly updates to HMRC.' } }, { '@type': 'Question', name: 'How is Zap different from QuickBooks or FreeAgent?', acceptedAnswer: { '@type': 'Answer', text: 'Zap has zero UI to learn. You send receipts via WhatsApp or email, and Zap extracts everything automatically — no dashboards, no manual categorisation.' } }, { '@type': 'Question', name: 'Does Zap submit to HMRC?', acceptedAnswer: { '@type': 'Answer', text: 'No. Zap captures receipts and prepares structured records. Quarterly updates and tax returns are submitted by you or your accountant through compatible filing software.' } }] }
            },
            'mtd-guide': {
                title: 'MTD 2026 Master Guide | Making Tax Digital for Sole Traders',
                description: 'The complete Making Tax Digital 2026 guide for sole traders and landlords. Understand mandation, digital links, bridging software and quarterly obligations.',
                canonical: 'https://zapexpenses.com/mtd-2026-guide',
                jsonld: null
            },
            help: {
                title: 'Help & How to Use Zap | ZapExpenses',
                description: 'Learn how to send receipts via WhatsApp, forward email invoices, and log mileage claims with ZapExpenses.',
                canonical: 'https://zapexpenses.com/help',
                jsonld: null
            },
            pricing: {
                title: 'Pricing | ZapExpenses — Free 30-Day Trial',
                description: 'Simple, transparent pricing for ZapExpenses. Start with a 30-day free trial, no credit card required.',
                canonical: 'https://zapexpenses.com/pricing',
                jsonld: null
            },
            'speed-test': {
                title: 'Zap vs Traditional Apps — 4x Faster | ZapExpenses',
                description: 'See how ZapExpenses processes expenses 4x faster than traditional accounting apps.',
                canonical: 'https://zapexpenses.com/speed-test',
                jsonld: null
            }
        };

        const meta = pageMeta[view] || pageMeta.home;
        document.title = meta.title;

        let descTag = document.querySelector('meta[name="description"]');
        if (descTag) descTag.setAttribute('content', meta.description);

        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
        canonical.setAttribute('href', meta.canonical);

        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) ogTitle.setAttribute('content', meta.title);
        const ogDesc = document.querySelector('meta[property="og:description"]');
        if (ogDesc) ogDesc.setAttribute('content', meta.description);
        const ogUrl = document.querySelector('meta[property="og:url"]');
        if (ogUrl) ogUrl.setAttribute('content', meta.canonical);

        const twTitle = document.querySelector('meta[name="twitter:title"]');
        if (twTitle) twTitle.setAttribute('content', meta.title);
        const twDesc = document.querySelector('meta[name="twitter:description"]');
        if (twDesc) twDesc.setAttribute('content', meta.description);

        const existing = document.getElementById('zap-jsonld');
        if (existing) existing.remove();
        if (meta.jsonld) {
            const script = document.createElement('script');
            script.id = 'zap-jsonld';
            script.type = 'application/ld+json';
            script.textContent = JSON.stringify(meta.jsonld);
            document.head.appendChild(script);
        }
    }, [view]);

    // Handle hash scrolling
    useEffect(() => {
        if (window.location.hash) {
            // Small delay to ensure the DOM is ready
            const timer = setTimeout(() => {
                const hashId = window.location.hash.replace('#', '');
                const el = document.getElementById(hashId);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [view]);

    const orgSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Zap Expenses",
        "url": "https://zapexpenses.com",
        "logo": "https://zapexpenses.com/logo.png",
        "sameAs": [
            "https://www.linkedin.com/company/zap-expenses",
            "https://find-and-update.company-information.service.gov.uk/company/zap-expenses",
            "https://www.yell.com/biz/zap-expenses-london/"
        ]
    };

    let breadcrumbPaths = [];
    if (view !== 'home' && view !== 'success') {
        const mtdSubPages = [
            'property-allowance', 'joint-property-checker',
            'mileage-calculator', 'exemption-criteria', 'foreign-property-income',
            'digital-links-explained', 'landlords-joint-ownership', 'property-expense-tree', 'speed-test'
        ];
        const partnerPages = ['partners-onboarding', 'partners-margin'];

        if (view === 'mtd-guide') {
            breadcrumbPaths = [
                { name: 'Home', href: '/' },
                { name: 'MTD 2026 Master Guide' }
            ];
        } else if (mtdSubPages.includes(view)) {
            breadcrumbPaths = [
                { name: 'Home', href: '/' },
                { name: 'MTD 2026 Master Guide', href: '/mtd-2026-guide' }
            ];
        } else if (partnerPages.includes(view)) {
            breadcrumbPaths = [
                { name: 'Home', href: '/' },
                { name: 'MTD 2026 Master Guide', href: '/mtd-2026-guide' },
                { name: 'Partners' }
            ];
        } else {
            // Default fallback (e.g. review, receipts, mileage, help, pricing)
            breadcrumbPaths = [
                { name: 'Home', href: '/' }
            ];
        }
    }

    return (
        <GoogleAnalytics>
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
            </Helmet>
            <div className="selection:bg-indigo-500/30 min-h-screen flex flex-col relative">
                <ConsentBanner />
                {view === 'home' && !user && <MTDStatusBanner />}
                {view !== 'home' && view !== 'success' && (
                    <nav className={`mx-auto w-full px-6 pt-10 pb-2 z-10 ${['property-allowance', 'property-expense-tree', 'joint-property-checker'].includes(view)
                        ? 'max-w-2xl'
                        : view === 'pricing' ? 'max-w-5xl'
                            : view === 'home' ? 'max-w-6xl' : 'max-w-4xl'
                        }`}>
                        <div className="flex items-center justify-between">
                            {view !== 'home' && (
                                <Breadcrumbs paths={breadcrumbPaths} />
                            )}

                            {view === 'home' && user && (
                                <div className="font-bold text-xl text-white tracking-tight hover:text-indigo-400 transition-colors cursor-pointer" onClick={() => setView('home')}>ZapExpenses</div>
                            )}

                            {user && (
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-4">
                                        <a href="/receipts" className={`text-sm font-semibold transition-all ${view === 'receipts' ? 'text-indigo-400' : 'text-slate-400 hover:text-white'}`}>Receipts</a>
                                        <a href="/mileage" className={`text-sm font-semibold transition-all ${view === 'mileage' ? 'text-indigo-400' : 'text-slate-400 hover:text-white'}`}>Mileage</a>
                                        <a href="/review" className={`text-sm font-semibold transition-all ${view === 'review' ? 'text-indigo-400' : 'text-slate-400 hover:text-white'}`}>Review</a>
                                        <a href="/help" className={`text-sm font-semibold transition-all ${view === 'help' ? 'text-indigo-400' : 'text-slate-400 hover:text-white'}`}>Help</a>
                                    </div>
                                    <div className="h-4 w-[1px] bg-white/10 mx-1"></div>
                                    <button onClick={handleLogout} className="text-slate-500 hover:text-white text-sm font-medium transition-colors">Log Out</button>
                                </div>
                            )}
                        </div>
                    </nav>
                )}

                {view === 'home' ? (
                    <>


                        <section className="max-w-4xl mx-auto pt-8 pb-12 px-6 text-center">
                            <div className="flex flex-wrap justify-center gap-3 mb-14">
                                <div className="inline-block px-4 py-1.5 text-sm font-semibold tracking-wide text-indigo-400 uppercase bg-indigo-500/10 rounded-full border border-indigo-500/20">
                                    {user ? 'Verified Account' : 'Receipt Capture for Sole Traders & Landlords'}
                                </div>
                                {!user && (
                                    <div className="inline-block px-4 py-1.5 text-sm font-semibold tracking-wide text-emerald-400 uppercase bg-emerald-500/10 rounded-full border border-emerald-500/20 shadow-lg shadow-emerald-500/5">
                                        Ditch paper receipts from £4.95
                                    </div>
                                )}
                            </div>
                            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-14">
                                {user ? (
                                    <>
                                        Your personal inbox is ready.
                                    </>
                                ) : (
                                    <>
                                        Digital records<br /> <span className="gradient-text">without the faff</span>
                                    </>
                                )}
                            </h1>
                            <p className="text-xl text-slate-400 mb-10 leading-relaxed max-w-2xl mx-auto">
                                {user ? (
                                    <>You are logged in as <span className="text-white">{user.email}</span>. Send receipts and mileage via WhatsApp or email as below.</>
                                ) : (
                                    <>
                                        {POSITIONING.hero} {POSITIONING.subHero}
                                        <span className="block mt-4 text-base text-slate-500">{POSITIONING.noSubmit}</span>
                                    </>
                                )}
                            </p>

                            {user ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                                    {/* WhatsApp Card - Primary */}
                                    <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-2xl">
                                        <div className="flex items-center gap-2 mb-4">
                                            <span className="text-2xl">💬</span>
                                            <p className="text-slate-400 text-sm font-mono uppercase tracking-wider">WhatsApp</p>
                                        </div>
                                        {user.phone_verified ? (
                                            <>
                                                <div className="bg-black/50 p-4 rounded-xl border border-white/5 mb-3">
                                                    <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Send receipts to:</p>
                                                    <code className="text-lg text-green-400 font-mono font-bold select-all break-all">
                                                        {user.business_whatsapp_number || '+447700900000'}
                                                    </code>
                                                </div>
                                                <div className="flex items-center gap-2 text-sm text-slate-400">
                                                    <span className="text-green-500 font-bold">✓ Verified.</span>
                                                    <span>Sending from: <span className="text-white font-mono">{user.phone}</span></span>
                                                </div>
                                            </>
                                        ) : user.phone ? (
                                            <>
                                                <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-xl mb-3">
                                                    <p className="text-amber-300 text-sm font-semibold mb-1">⏳ Verification pending</p>
                                                    <p className="text-slate-400 text-xs">Check your WhatsApp for a verification link.</p>
                                                </div>
                                                <button
                                                    onClick={handleResendVerify}
                                                    disabled={whatsappAction === 'loading'}
                                                    className="w-full py-2.5 bg-white/5 border border-white/10 text-white text-sm font-semibold rounded-xl hover:bg-white/10 transition-all disabled:opacity-50"
                                                >
                                                    {whatsappAction === 'loading' ? 'Sending...' : 'Resend Verification'}
                                                </button>
                                                {whatsappMessage && <p className={`mt-2 text-xs text-center ${whatsappAction === 'error' ? 'text-red-400' : 'text-green-400'}`}>{whatsappMessage}</p>}
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-slate-400 text-sm mb-3">Add your number to send receipts and mileage via WhatsApp.</p>
                                                <form onSubmit={handleAddWhatsApp} className="space-y-2">
                                                    <input
                                                        type="tel"
                                                        value={addPhone}
                                                        onChange={(e) => setAddPhone(e.target.value)}
                                                        placeholder="+44 7700 900000"
                                                        className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 focus:outline-none focus:ring-2 focus:ring-green-500 text-white placeholder-slate-500 text-sm"
                                                    />
                                                    <button
                                                        type="submit"
                                                        disabled={whatsappAction === 'loading'}
                                                        className="w-full py-2.5 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-500 transition-all disabled:opacity-50"
                                                    >
                                                        {whatsappAction === 'loading' ? 'Setting up...' : 'Add WhatsApp'}
                                                    </button>
                                                </form>
                                                {whatsappMessage && <p className={`mt-2 text-xs text-center ${whatsappAction === 'error' ? 'text-red-400' : 'text-green-400'}`}>{whatsappMessage}</p>}
                                            </>
                                        )}
                                    </div>

                                    {/* Email Card - Secondary */}
                                    <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-2xl">
                                        <div className="flex items-center gap-2 mb-4">
                                            <span className="text-2xl">📧</span>
                                            <p className="text-slate-400 text-sm font-mono uppercase tracking-wider">Email</p>
                                        </div>
                                        {user.email_verified && user.ingestion_email ? (
                                            <>
                                                <div className="bg-black/50 p-4 rounded-xl border border-white/5 mb-3">
                                                    <code className="text-lg text-indigo-400 font-mono font-bold select-all break-all">
                                                        {user.ingestion_email}
                                                    </code>
                                                </div>
                                                <p className="text-slate-500 text-sm">
                                                    <span className="text-green-500 font-bold">✓ Verified.</span> Forward invoices to the above address.
                                                </p>
                                            </>
                                        ) : (
                                            <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-xl">
                                                <p className="text-amber-300 text-sm font-semibold mb-1">⏳ Verification pending</p>
                                                <p className="text-slate-400 text-xs">Check your email for a verification link.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-6">
                                    <div className="flex flex-col sm:flex-row items-center sm:items-stretch justify-center gap-8 sm:gap-12 w-full mt-8 max-w-6xl mx-auto">
                                        {/* Box 1 */}
                                        <div className="flex flex-col items-center gap-4 w-full sm:w-[220px] transition-transform hover:scale-[1.02]">
                                            <a
                                                href="#signup"
                                                onClick={() => trackEvent('whatsapp_conversion_start', { source: 'hero_cta' })}
                                                className="px-6 py-4 bg-white text-slate-950 font-bold rounded-xl hover:bg-slate-200 transition-all w-full text-center h-full flex items-center justify-center shadow-xl whitespace-nowrap"
                                            >
                                                Start Your Free Month
                                            </a>
                                            <div className="flex items-start justify-center gap-2 text-sm text-slate-500 min-h-[40px]">
                                                <span className="mt-[2px]">⚡</span>
                                                <div className="text-center">
                                                    We ship fast and<br />
                                                    love feedback
                                                </div>
                                            </div>
                                        </div>

                                        {/* Box 2 */}
                                        <div className="flex flex-col items-center gap-4 w-full sm:w-[220px] transition-transform hover:scale-[1.02]">
                                            <a href="/help" className="px-6 py-4 bg-indigo-600/30 text-indigo-100 font-bold rounded-xl hover:bg-indigo-600/50 transition-all border border-indigo-400/60 hover:border-indigo-300 w-full text-center h-full flex items-center justify-center shadow-lg shadow-indigo-500/10 whitespace-nowrap">
                                                Read the Help Guide
                                            </a>
                                            <div className="flex items-start justify-center gap-2 text-sm text-slate-500 min-h-[40px]">
                                                <span className="mt-[2px]">💬</span>
                                                <div className="text-center">
                                                    Questions? We're a real<br />
                                                    team: <a href={`mailto:${config.SUPPORT_EMAIL}`} className="text-indigo-400 hover:text-indigo-300 transition-colors underline">drop us an email</a>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Box 3 */}
                                        <div className="flex flex-col items-center gap-4 w-full sm:w-[220px] transition-transform hover:scale-[1.02]">
                                            <a href="/mtd-2026-guide" className="px-6 py-4 bg-emerald-600/20 text-emerald-100 font-bold rounded-xl hover:bg-emerald-600/40 transition-all border border-emerald-500/40 hover:border-emerald-400 w-full text-center h-full flex items-center justify-center shadow-lg shadow-emerald-500/10 whitespace-nowrap">
                                                Full MTD 2026 Guide
                                            </a>
                                            <div className="flex items-start justify-center gap-2 text-sm text-slate-500 min-h-[40px]">
                                                <span className="mt-[2px]">🔨</span>
                                                <div className="text-center">
                                                    Built for<br />
                                                    MTD 2026
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </section>

                        {!user && (
                            <section className="max-w-[53.6rem] mx-auto px-6 pt-10 pb-5">
                                <div className="grid md:grid-cols-[3fr_2fr] gap-6 items-stretch">
                                    <div id="stat-leakage-2026" className="bg-black/40 border border-slate-800 p-5 rounded-3xl shadow-inner relative overflow-hidden text-center md:text-left flex flex-col justify-center">
                                        <p className="text-emerald-400 font-bold uppercase tracking-widest text-[10px] mb-1.5">Workflow Bottlenecks</p>
                                        <p className="text-2xl font-extrabold text-white mb-3">The Unclaimed Expense Crisis</p>
                                        <p className="text-slate-400 text-sm leading-relaxed mb-3">
                                            "UK sole traders leave £962m in expenses unclaimed annually - approx. £123.00 per individual - with 33% citing 'lost receipts' as the primary barrier to claiming."
                                        </p>
                                        <div className="mt-auto text-[10px] text-slate-500 italic border-t border-slate-800/50 pt-3 text-center md:text-left">
                                            Source: <a href="https://www.gosimpletax.com/insights/uk-expense-statistics/" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400 transition-colors">GoSimpleTax, UK Expense Statistics</a>
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-br from-indigo-900/40 to-slate-900 border border-indigo-500/20 p-5 rounded-3xl shadow-2xl relative overflow-hidden flex flex-col justify-center text-center min-h-[220px]">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl pointer-events-none"></div>
                                        <p className="text-slate-300 text-base mb-4 max-w-xs mx-auto leading-relaxed">
                                            See how our zero-faff approach compares to mainstream accounting apps.
                                        </p>
                                        <a href="/why-choose-zap" className="inline-block px-8 py-4 bg-indigo-600 text-white font-extrabold rounded-2xl shadow-xl shadow-indigo-500/30 hover:bg-indigo-500 hover:scale-105 active:scale-95 transition-all duration-300 tracking-tight text-xl mx-auto">
                                            Why Choose Zap?
                                        </a>
                                    </div>
                                </div>
                            </section>
                        )}

                        <div className="max-w-[53.6rem] mx-auto border-t border-white/5 my-4"></div>


                        <section id="how-it-works" className="max-w-5xl mx-auto pt-[4.5rem] pb-20 px-6">
                            <div className="flex flex-col md:flex-row items-center justify-center gap-x-12 gap-y-6 mb-10 border-b border-white/5 pb-8">
                                <h2 className="text-3xl font-bold flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
                                    <span>{user ? 'How to use your inbox' : 'How it works'}</span>
                                    <a href="/help" className="text-sm font-medium text-emerald-400 hover:text-emerald-300 transition-colors mt-1 sm:mt-0 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 relative top-0 sm:top-[3px]">
                                        (full guide here)
                                    </a>
                                </h2>
                                <a href="/speed-test" className="group flex items-center gap-2 px-5 py-2.5 bg-indigo-500/5 border border-white/10 rounded-full text-sm font-bold text-slate-400 hover:text-indigo-400 hover:border-indigo-500/30 transition-all shadow-inner">
                                    <span className="group-hover:animate-pulse text-lg">⏱️</span>
                                    See why Zap is 4x faster than traditional apps →
                                </a>
                            </div>

                            {/* Steps row */}
                            <div className="flex flex-col sm:flex-row gap-8 mb-12 max-w-[53.6rem] mx-auto">
                                <div className="flex flex-col items-center sm:items-start gap-4 sm:basis-1/4 text-center sm:text-left">
                                    <div className="text-2xl mb-1 font-bold text-white font-mono">1: 📸</div>
                                    <p className="text-slate-300 text-[17.8px] leading-relaxed">Snap a receipt photo, forward an email invoice, or text a quick mileage claim.</p>
                                </div>
                                <div className="flex flex-col items-center sm:items-start gap-4 sm:basis-1/4 text-center sm:text-left">
                                    <div className="text-2xl mb-1 font-bold text-white font-mono">2: 🤖</div>
                                    <p className="text-slate-300 text-[17.8px] leading-relaxed">Our AI instantly extracts the details and flags any missing info for review.</p>
                                </div>
                                <div className="flex flex-col items-center sm:items-start gap-4 sm:basis-1/2 text-center sm:text-left">
                                    <div className="text-2xl mb-1 font-bold text-white font-mono">3: 📄</div>
                                    <p className="text-slate-300 text-[17.8px] leading-relaxed">Receive a structured CSV and receipt archive each month, shared with your accountant or ready for import into your accounting or filing software.</p>
                                </div>
                            </div>

                            {/* Two Chat Graphics side by side */}
                            <div className="flex flex-col md:flex-row justify-center gap-20 items-stretch">

                                {/* Receipt chat graphic */}
                                <div className="flex-1 max-w-[24.3rem] bg-[#0b141a] border border-white/5 pt-4 pb-6 px-4 rounded-2xl text-sm shadow-2xl flex flex-col relative overflow-hidden">
                                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://whatsapp-clone-react-js.netlify.app/static/media/whatsapp-back.0505199a.png')] bg-repeat"></div>
                                    <div className="text-white flex items-center gap-3 mb-2 border-b border-white/5 pb-3 relative z-10">
                                        <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs">ZE</div>
                                        <div className="flex flex-col">
                                            <span className="font-semibold leading-none">ZapExpenses</span>
                                            <span className="text-[10px] text-green-500 mt-1">online</span>
                                        </div>
                                    </div>
                                    <div className="flex-1 flex flex-col justify-end space-y-2 relative z-10 pt-3">
                                        <div className="flex flex-col items-end">
                                            <div className="bg-[#005c4b] p-2 rounded-lg rounded-tr-none mb-0.5 shadow-sm max-w-[85%]">
                                                <div className="bg-black/20 px-3 py-2 rounded flex items-center gap-2">
                                                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    <span className="text-xs font-medium text-white font-sans">Photo</span>
                                                </div>
                                                <div className="flex items-center justify-end gap-1 mt-1">
                                                    <span className="text-[10px] text-white/60 font-sans">14:20</span>
                                                    <span className="text-sky-400 text-[10px]">✓✓</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col items-start space-y-1">
                                            <div className="bg-[#202c33] text-slate-200 px-3 py-1.5 rounded-lg rounded-tl-none max-w-[88%] shadow-sm border border-white/5">
                                                <div className="flex items-center gap-2 mb-1.5 text-green-400 font-semibold text-[13px] font-sans">
                                                    <span>✅</span> Receipt received!
                                                </div>
                                                <div className="bg-black/20 p-2 rounded text-[11px] text-white flex items-center gap-2 border border-white/5 font-sans">
                                                    <span>🧾</span> Screwfix | £55.87 | 2026-02-07
                                                </div>
                                                <div className="text-[10px] text-slate-400 text-right mt-1 font-sans">14:20</div>
                                            </div>

                                            <div className="bg-[#202c33] text-slate-200 px-3 py-1.5 rounded-lg rounded-tl-none max-w-[98%] shadow-sm border border-white/5">
                                                <div className="flex items-center gap-2 mb-1.5 text-amber-400 font-semibold text-[13px] font-sans">
                                                    <span>⚠️</span> Some details need review:
                                                </div>
                                                <ul className="list-disc ml-4 mb-2 text-xs text-slate-300 font-sans">
                                                    <li>Date is not recent (verifying)</li>
                                                </ul>
                                                <div className="text-xs text-indigo-400 font-medium font-sans">
                                                    👉 Fix here: <span className="underline cursor-pointer break-all">zapexpenses.com/review/a9f2...</span>
                                                </div>
                                                <div className="text-[10px] text-slate-400 text-right mt-1 font-sans">14:20</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Mileage chat graphic */}
                                <div className="flex-1 max-w-[24.3rem] bg-[#0b141a] border border-white/5 pt-4 pb-6 px-4 rounded-2xl text-sm shadow-2xl flex flex-col relative overflow-hidden font-sans">
                                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://whatsapp-clone-react-js.netlify.app/static/media/whatsapp-back.0505199a.png')] bg-repeat"></div>
                                    <div className="text-white flex items-center gap-3 mb-3 border-b border-white/5 pb-3 relative z-10 font-sans">
                                        <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs">ZE</div>
                                        <div className="flex flex-col font-sans">
                                            <span className="font-semibold leading-none">ZapExpenses</span>
                                            <span className="text-[10px] text-green-500 mt-1">online</span>
                                        </div>
                                    </div>
                                    <div className="flex-1 flex flex-col justify-end space-y-2 relative z-10 font-sans pt-3">
                                        <div className="flex flex-col items-end">
                                            <div className="bg-[#005c4b] px-3 py-2 rounded-lg rounded-tr-none text-white text-xs shadow-sm max-w-[85%] font-sans">
                                                Drove from LE5 3AD to N18 2FP for conference
                                                <div className="flex items-center justify-end gap-1 mt-1 font-sans">
                                                    <span className="text-[10px] text-white/60 font-sans">09:12</span>
                                                    <span className="text-sky-400 text-[10px]">✓✓</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col items-start space-y-2">
                                            <div className="bg-[#202c33] text-slate-200 px-3 py-1.5 rounded-lg rounded-tl-none max-w-[94%] shadow-sm border border-white/5 font-sans">
                                                <div className="flex items-center gap-2 mb-1 text-green-400 font-semibold text-[13px] font-sans">
                                                    <span>✅</span> Mileage logged!
                                                </div>
                                                <div className="bg-black/20 p-2 rounded text-[11px] text-white flex items-center gap-2 border border-white/5 mb-1 font-sans">
                                                    <span>🚗</span> 118.4 mi · Personal Car · 2026-03-16
                                                </div>
                                                <div className="text-[10px] text-slate-400 text-right font-sans">09:12</div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col items-end w-full mt-2">
                                            <div className="bg-[#005c4b] px-3 py-2 rounded-lg rounded-tr-none text-white text-xs shadow-sm max-w-[85%] font-sans">
                                                Can I get this month's report?
                                                <div className="flex items-center justify-end gap-1 mt-1 font-sans">
                                                    <span className="text-[10px] text-white/60 font-sans">09:13</span>
                                                    <span className="text-sky-400 text-[10px]">✓✓</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col items-start space-y-1">
                                            <div className="bg-[#202c33] text-slate-200 px-3 py-1.5 rounded-lg rounded-tl-none max-w-[94%] shadow-sm border border-white/5 font-sans">
                                                <div className="flex items-center gap-2 mb-1 text-indigo-400 font-semibold text-[13px] font-sans">
                                                    <span>📄</span> Report on its way!
                                                </div>
                                                <p className="text-xs text-slate-300 font-sans">Your March report is prepared and will be emailed to you shortly.</p>
                                                <div className="text-[10px] text-slate-400 text-right mt-1 font-sans">09:13</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {!user && (
                            <>
                                <div className="max-w-[53.6rem] mx-auto border-t border-white/5 my-6"></div>
                                <section id="signup" className="max-w-[30rem] mx-auto pt-6 pb-7 px-6">
                                    <div className="text-center mb-10">
                                        <h2 className="text-3xl font-bold mb-3">Expense tracking, solved</h2>
                                        <p className="text-slate-400 text-lg leading-relaxed">
                                            No need to hoard receipts or ever touch a spreadsheet again. Our extraction engine is highly accurate, saving you the hassle of tedious admin work. Records are kept while your account is active so you can export them at any time. Experience the difference completely free for 30 days. No commitment, no credit card required.
                                        </p>
                                    </div>

                                    <div className="bg-gradient-to-br from-indigo-900/40 via-slate-900 to-slate-950 p-8 rounded-3xl border border-white/10 shadow-2xl">
                                        <h2 className="text-3xl font-bold mb-8 text-center text-white">Start Your Free Month</h2>

                                        {signupStatus === 'success' ? (
                                            <div className="p-6 bg-green-500/20 border border-green-500/50 rounded-xl text-center text-green-200">
                                                <h3 className="text-xl font-bold mb-3">You're almost there!</h3>
                                                {signupMessage}
                                                <div className="mt-6 pt-6 border-t border-white/5">
                                                    <p className="text-xs text-slate-400 mb-3">Didn't get the email?</p>
                                                    <button
                                                        onClick={handleResendSignup}
                                                        disabled={resendTimer > 0 || resendStatus === 'loading'}
                                                        className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm font-semibold hover:bg-white/10 transition-all disabled:opacity-50"
                                                    >
                                                        {resendStatus === 'loading' ? 'Sending...' :
                                                            resendTimer > 0 ? `Resend again in ${resendTimer}s` :
                                                                resendStatus === 'success' ? 'Email Sent Again!' : 'Resend Email'}
                                                    </button>
                                                    {resendStatus === 'error' && (
                                                        <p className="mt-2 text-xs text-red-400">Failed to resend. Please try again later.</p>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <form onSubmit={handleSignup} className="space-y-4">
                                                <div>
                                                    <label className="block text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5 ml-1">Email address *</label>
                                                    <input
                                                        type="email"
                                                        required
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        placeholder="you@example.com"
                                                        className="w-full px-6 py-4 rounded-xl bg-black/30 border border-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white placeholder-slate-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5 ml-1">WhatsApp number <span className="text-slate-600 normal-case">(optional)</span></label>
                                                    <input
                                                        type="tel"
                                                        value={phone}
                                                        onChange={(e) => setPhone(e.target.value)}
                                                        placeholder="+44 7700 900000"
                                                        className="w-full px-6 py-4 rounded-xl bg-black/30 border border-white/10 focus:outline-none focus:ring-2 focus:ring-green-500 text-white placeholder-slate-500"
                                                    />
                                                    <p className="text-[11px] text-slate-500 mt-1.5 ml-1">Add your number to snap receipts and log mileage via WhatsApp.</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5 ml-1">Accountant Email <span className="text-slate-600 normal-case">(optional)</span></label>
                                                    <input
                                                        type="email"
                                                        value={accountantEmail}
                                                        onChange={(e) => setAccountantEmail(e.target.value)}
                                                        placeholder="accountant@example.com"
                                                        className="w-full px-6 py-4 rounded-xl bg-black/30 border border-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white placeholder-slate-500"
                                                    />
                                                    <p className="text-[11px] text-slate-500 mt-1.5 ml-1">Cc your accountant in your monthly reports.</p>
                                                </div>
                                                {signupStatus === 'error' && <p className="text-red-400 text-sm text-center">{signupMessage}</p>}
                                                <button
                                                    type="submit"
                                                    disabled={signupStatus === 'loading'}
                                                    className="w-full py-4 bg-white text-black font-bold rounded-xl shadow-lg hover:bg-slate-200 transition-all uppercase tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {signupStatus === 'loading' ? 'Setting Up...' : 'Start Capturing'}
                                                </button>

                                                <div className="mt-6 pt-6 border-t border-white/5 text-center">
                                                    <p className="text-sm text-slate-400">
                                                        Plans start at <span className="text-white font-semibold">£4.95/month</span> after trial ends.
                                                    </p>
                                                    <a
                                                        href="/pricing"
                                                        className="inline-block text-indigo-400 hover:text-indigo-300 text-sm font-semibold transition-colors mt-1"
                                                    >
                                                        View all pricing details →
                                                    </a>
                                                </div>
                                            </form>
                                        )}
                                    </div>
                                </section>

                                <div className="max-w-[30rem] mx-auto pb-12 px-6 text-center">
                                    <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-sm text-slate-500 mt-5">
                                        <div className="flex items-start gap-2">
                                            <span className="mt-[2px]">💬</span>
                                            <div className="text-center">
                                                Questions? We're a real<br />
                                                team: <a href="mailto:hello@zapexpenses.com" className="text-indigo-400 hover:text-indigo-300 transition-colors underline">drop us an email</a>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <span className="mt-[2px]">⚡</span>
                                            <div className="text-center">
                                                We ship fast and<br />
                                                love feedback
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                ) : view === 'mockreview' ? (
                    <section className="max-w-4xl mx-auto py-12 px-6">
                        <MockReviewPage />
                    </section>
                ) : view === 'receipts' ? (
                    <section className="max-w-6xl mx-auto py-12 px-6">
                        <ReceiptsPage />
                    </section>
                ) : view === 'mockreceipts' ? (
                    <section className="max-w-6xl mx-auto py-12 px-6">
                        <MockReceiptsPage />
                    </section>
                ) : view === 'mileage' ? (
                    <section className="max-w-6xl mx-auto py-12 px-6">
                        <MileagePage />
                    </section>
                ) : view === 'mockmileage' ? (
                    <section className="max-w-6xl mx-auto py-12 px-6">
                        <MockMileagePage />
                    </section>
                ) : view === 'pricing' ? (
                    <section className="max-w-6xl mx-auto py-12 px-6">
                        <PricingPage setView={setView} />
                    </section>
                ) : view === 'verify_expired' ? (
                    <VerifyExpiredPage />
                ) : view === 'help' ? (
                    <HelpPage />
                ) : view === 'why-choose-zap' ? (
                    <section className="pb-20">
                      <WhyChooseZap isEmbedded={false} />
                    </section>
                ) : view === 'mtd-guide' ? (
                    <MtdGuidePage />
                ) : view === 'property-allowance' ? (
                    <PropertyDecisionTree />
                ) : view === 'mileage-calculator' ? (
                    <MileageComparator />
                ) : view === 'speed-test' ? (
                    <EfficiencyTimer />
                ) : view === 'partners-onboarding' ? (
                    <OnboardingGuide />
                ) : view === 'partners-margin' ? (
                    <MarginCalculator />
                ) : view === 'joint-property-checker' ? (
                    <JointPropertyChecker />
                ) : view === 'foreign-property-income' ? (
                    <ForeignPropertyIncome />
                ) : view === 'landlords-joint-ownership' ? (
                    <LandlordsJointOwnership />
                ) : view === 'digital-links-explained' ? (
                    <DigitalLinksExplained />
                ) : view === 'exemption-criteria' ? (
                    <ExemptionCriteria />
                ) : view === 'success' ? (
                    <SuccessPage setView={setView} />
                ) : view === 'logout' ? (
                    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                        <div className="text-indigo-500 animate-pulse">Logging out...</div>
                    </div>
                ) : view === 'mock_index' ? (
                    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6">
                        <div className="max-w-md w-full bg-slate-900 border border-indigo-500/30 rounded-2xl p-8 shadow-2xl">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="bg-amber-500/20 text-amber-300 text-xs px-2 py-0.5 rounded border border-amber-500/30 font-mono tracking-wider">DEBUG</span>
                                <h1 className="text-2xl font-bold text-white">Mock Pages</h1>
                            </div>
                            <ul className="space-y-3">

                                <li>
                                    <a href="/mockreview" className="block p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-indigo-500 hover:bg-slate-800/80 transition group">
                                        <div className="font-bold text-indigo-400 group-hover:text-indigo-300">Mock Review</div>
                                        <div className="text-xs text-slate-500">Expense review interface</div>
                                    </a>
                                </li>
                                <li>
                                    <a href="/mockreceipts" className="block p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-indigo-500 hover:bg-slate-800/80 transition group">
                                        <div className="font-bold text-indigo-400 group-hover:text-indigo-300">Mock Receipts</div>
                                        <div className="text-xs text-slate-500">Receipt history and status</div>
                                    </a>
                                </li>
                                <li>
                                    <a href="/mockmileage" className="block p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-indigo-500 hover:bg-slate-800/80 transition group">
                                        <div className="font-bold text-indigo-400 group-hover:text-indigo-300">Mock Mileage</div>
                                        <div className="text-xs text-slate-500">Mileage logging and editing</div>
                                    </a>
                                </li>
                                <li>
                                    <a href="/success" className="block p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30 hover:border-emerald-500 hover:bg-emerald-500/20 transition group">
                                        <div className="font-bold text-emerald-400 group-hover:text-emerald-300">Success Page</div>
                                        <div className="text-xs text-slate-500">Post-purchase thank you page</div>
                                    </a>
                                </li>
                            </ul>
                            <div className="mt-6 text-center text-xs text-slate-600 font-mono">
                                ENABLE_MOCKS = true
                            </div>
                        </div>
                    </div>
                ) : (
                    <section className="max-w-4xl mx-auto py-12 px-6">
                        <ReviewPage />
                    </section>
                )
                }

                <footer className="max-w-4xl mx-auto py-12 px-6 border-t border-slate-800 text-slate-500 text-sm flex flex-col md:flex-row justify-between items-center gap-4">
                    <div>Zap Expenses. Receipt capture & structured records. <span className="text-emerald-500/70">🔒 Encrypted & Secure.</span></div>
                    <div className="flex gap-6">
                        <a href="/privacy.html" className="hover:text-white">Privacy</a>
                        <a href="/terms.html" className="hover:text-white">Terms</a>
                        <a href="/help" className="hover:text-white">Help</a>
                        <a href={`mailto:${config.SUPPORT_EMAIL}`} className="hover:text-white">Contact</a>
                    </div>
                </footer>
            </div>
        </GoogleAnalytics>
    );
}

export default App;